import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { ORDER_POLL_INTERVAL_MS } from "../constants/cryptoSwaps.constants";

/** What is known about ONE id. Compared against the requested id during render, never synchronised. */
interface Snapshot<T> {
  orderId: string;
  order: T | null;
  /** The last refresh failed; the record on screen is the previous one. */
  isStale: boolean;
  notFound: boolean;
  lastUpdatedAt: Date | null;
}

export interface UseOtcOrderResult<T> {
  order: T | null;
  isLoading: boolean;
  isRefreshing: boolean;
  isStale: boolean;
  notFound: boolean;
  lastUpdatedAt: Date | null;
  refresh: () => void;
}

/**
 * One record, polled while it can still move. A failed refresh keeps the last copy and says it is stale.
 * `fetchOrder` is a dependency: pass a module-level service function.
 */
export function useOtcOrderPoller<T>(
  orderId: string | null,
  fetchOrder: (orderId: string) => Promise<T>,
  isLive: (order: T) => boolean,
): UseOtcOrderResult<T> {
  const [snapshot, setSnapshot] = useState<Snapshot<T> | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reloadToken, requestReload] = useReducer((token: number) => token + 1, 0);
  /** Only the latest request may write: an older answer landing late would roll the status back. */
  const latestRequestRef = useRef(0);
  const inFlightRef = useRef(false);

  const current = snapshot?.orderId === orderId ? snapshot : null;
  const order = current?.order ?? null;

  const load = useCallback(
    async (orderIdToLoad: string, background: boolean, isCancelled: () => boolean) => {
      const requestId = ++latestRequestRef.current;
      const isSuperseded = () => isCancelled() || requestId !== latestRequestRef.current;
      inFlightRef.current = true;
      if (background) setIsRefreshing(true);
      try {
        const next = await fetchOrder(orderIdToLoad);
        if (isSuperseded()) return;
        setSnapshot({
          orderId: orderIdToLoad,
          order: next,
          isStale: false,
          notFound: false,
          lastUpdatedAt: new Date(),
        });
      } catch (error: unknown) {
        if (isSuperseded()) return;
        const notFound = HttpApiService.isNotFoundError(error);
        setSnapshot((previous) => {
          const same = previous?.orderId === orderIdToLoad;
          return {
            orderId: orderIdToLoad,
            order: notFound ? null : same ? previous.order : null,
            isStale: !notFound,
            notFound,
            lastUpdatedAt: same ? previous.lastUpdatedAt : null,
          };
        });
      } finally {
        if (requestId === latestRequestRef.current) {
          inFlightRef.current = false;
          if (!isCancelled()) setIsRefreshing(false);
        }
      }
    },
    [fetchOrder],
  );

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    void load(orderId, false, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [orderId, load, reloadToken]);

  const canPoll = !!orderId && !current?.notFound && (!order || isLive(order));

  useEffect(() => {
    if (!orderId || !canPoll) return;
    let cancelled = false;
    const intervalId = window.setInterval(() => {
      if (inFlightRef.current) return;
      void load(orderId, true, () => cancelled);
    }, ORDER_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [orderId, canPoll, load]);

  return useMemo(
    () => ({
      order,
      isLoading: !!orderId && current === null,
      isRefreshing,
      isStale: current?.isStale ?? false,
      notFound: current?.notFound ?? false,
      lastUpdatedAt: current?.lastUpdatedAt ?? null,
      refresh: requestReload,
    }),
    [order, orderId, current, isRefreshing],
  );
}
